import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  PhotoIcon,
  TrashIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import ImageManagerModal from "./ImageManagerModal";

const FONT_TNR = "";
const BASE_MD_WIDTH = 1000;
const DEFAULT_BG_RATIO = 1451 / 1024;

/* ---------- UI primitives ---------- */
const Input = (props) => (
  <input
    {...props}
    className={`input input-bordered w-full ${props.className || ""}`}
  />
);

const Select = (props) => (
  <select
    {...props}
    className={`select select-bordered w-full ${props.className || ""}`}
  />
);

/* ---------- ScaledStage ---------- */
function useScaledStage(baseWidth = BASE_MD_WIDTH) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [frameSize, setFrameSize] = useState({ w: baseWidth, h: 0 });

  const update = () => {
    const availW = outerRef.current?.clientWidth || baseWidth;
    const s = Math.min(1, availW / baseWidth);
    setScale(s);

    if (innerRef.current) {
      const h = innerRef.current.offsetHeight || 0;
      setFrameSize({ w: baseWidth * s, h: h * s });
    }
  };

  useLayoutEffect(() => {
    update();

    const ro =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;

    if (outerRef.current && ro) ro.observe(outerRef.current);
    if (innerRef.current && ro) ro.observe(innerRef.current);

    window.addEventListener("resize", update);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseWidth]);

  return { outerRef, innerRef, scale, frameSize };
}

function ScaledStage({ children }) {
  const { outerRef, innerRef, scale, frameSize } = useScaledStage();

  return (
    <div ref={outerRef} style={{ width: "100%" }}>
      <div
        style={{
          width: frameSize.w,
          height: frameSize.h,
          margin: "0 auto",
          overflow: "hidden",
        }}
      >
        <div
          ref={innerRef}
          style={{
            width: BASE_MD_WIDTH,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/* ---------- Helpers ---------- */
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const toPct = (px, base) => (base ? (px / base) * 100 : 0);
const squareHeightPct = (widthPct) =>
  Number((Number(widthPct || 0) / DEFAULT_BG_RATIO).toFixed(2));

export const safeParseEventInvitationSchema = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;

  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  return null;
};

export const genEventInvitationLayerId = (prefix = "layer") =>
  `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;

/* =========================================================
   DEFAULT_SCHEMA
========================================================= */
export const DEFAULT_EVENT_INVITATION_SCHEMA = {
  schemaVersion: 2,
  backgroundSrc: "/event_default_bg.png",
  layers: [],
};

const EVENT_TEMPLATE_VARS = {
  title: { token: "{{title}}", preview: "THƯ MỜI THAM DỰ" },
  topic: { token: "{{topic}}", preview: "HỘI THẢO KẾT NỐI DOANH NGHIỆP" },
  time: { token: "{{time}}", preview: "08:00, ngày 20/12/2026" },
  location: {
    token: "{{location}}",
    preview: "Trung tâm Hội nghị Thành phố Cần Thơ",
  },
  note: {
    token: "{{note}}",
    preview: "Vui lòng đăng ký trước khi tham dự.",
  },
  content: {
    token: "{{content}}",
    preview:
      "08:00  Đón khách\n08:30  Khai mạc chương trình\n09:00  Giao lưu kết nối\n10:30  Tổng kết",
  },
  qrCode: {
    token: "{{qrCode}}",
    preview: "",
  },
};

const MOCK_EVENT = {
  title: EVENT_TEMPLATE_VARS.title.preview,
  topic: EVENT_TEMPLATE_VARS.topic.preview,
  time: EVENT_TEMPLATE_VARS.time.preview,
  location: EVENT_TEMPLATE_VARS.location.preview,
  note: EVENT_TEMPLATE_VARS.note.preview,
  content: EVENT_TEMPLATE_VARS.content.preview,
  qrCode: EVENT_TEMPLATE_VARS.qrCode.preview,
};

const FIELD_DEFS = [
  {
    key: "event.title",
    label: "Sự kiện: Tiêu đề",
    prefix: "",
  },
  {
    key: "event.topic",
    label: "Sự kiện: Chủ đề",
    prefix: "",
  },
  {
    key: "event.time",
    label: "Sự kiện: Thời gian",
    prefix: "Thời gian: ",
  },
  {
    key: "event.location",
    label: "Sự kiện: Địa điểm",
    prefix: "Địa điểm: ",
  },
  {
    key: "event.content",
    label: "Sự kiện: Nội dung chương trình",
    prefix: "",
  },
  {
    key: "event.note",
    label: "Sự kiện: Ghi chú",
    prefix: "",
  },
];

const IMAGE_SOURCES = [
  { key: "", label: "Nhập đường dẫn ảnh" },
  { key: "event.qrCode", label: "Sự kiện: Ảnh QR đăng ký" },
];

const resolveFieldValue = (key) => {
  switch (key) {
    case "event.title":
      return EVENT_TEMPLATE_VARS.title.token;
    case "event.topic":
      return EVENT_TEMPLATE_VARS.topic.token;
    case "event.time":
      return EVENT_TEMPLATE_VARS.time.token;
    case "event.location":
      return EVENT_TEMPLATE_VARS.location.token;
    case "event.content":
      return EVENT_TEMPLATE_VARS.content.token;
    case "event.note":
      return EVENT_TEMPLATE_VARS.note.token;
    default:
      return "—";
  }
};

const resolveImageSource = (key, eventData = MOCK_EVENT) => {
  if (!key) return "";
  if (key === "event.qrCode") return eventData?.qrCode || "";
  return "";
};

const getImagePreviewText = (layer) => {
  if (layer?.sourceKey === "event.qrCode") return "Ảnh QR";

  return resolveImageSource(layer?.sourceKey, MOCK_EVENT) || layer?.src || "—";
};

const renderTemplatePreviewText = (text = "", eventData = MOCK_EVENT) =>
  String(text || "").replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (m, key) => {
    if (key === "title") {
      return eventData?.title ?? EVENT_TEMPLATE_VARS.title.preview;
    }
    if (key === "topic") {
      return eventData?.topic ?? EVENT_TEMPLATE_VARS.topic.preview;
    }
    if (key === "time") {
      return eventData?.time ?? EVENT_TEMPLATE_VARS.time.preview;
    }
    if (key === "location") {
      return eventData?.location ?? EVENT_TEMPLATE_VARS.location.preview;
    }
    if (key === "note") {
      return eventData?.note ?? EVENT_TEMPLATE_VARS.note.preview;
    }
    if (key === "content") {
      return eventData?.content ?? EVENT_TEMPLATE_VARS.content.preview;
    }

    return m;
  });

export function normalizeEventInvitationLayer(layer) {
  const type = layer?.type === "image" ? "image" : "text";
  const style = layer?.style || {};
  const isQrLayer = type === "image" && layer?.sourceKey === "event.qrCode";
  const square = type === "image" ? !!layer?.square || isQrLayer : undefined;
  const width = Number(layer?.width ?? 20);

  const out = {
    id: layer?.id || genEventInvitationLayerId(type === "image" ? "img" : "txt"),
    type,

    left: Number(layer?.left ?? 0),
    width,
    height:
      type === "image" && square
        ? squareHeightPct(width)
        : Number(layer?.height ?? 6),
    zIndex: Number(layer?.zIndex ?? 10),

    top: layer?.bottom != null ? undefined : Number(layer?.top ?? 0),
    bottom: layer?.bottom != null ? Number(layer?.bottom) : undefined,

    text: type === "text" ? String(layer?.text ?? "") : undefined,

    src: type === "image" ? String(layer?.src ?? "") : undefined,
    fit:
      type === "image"
        ? layer?.fit === "contain"
          ? "contain"
          : "cover"
        : undefined,
    circle: type === "image" ? !!layer?.circle : undefined,
    square: type === "image" ? square : undefined,
    sourceKey: type === "image" ? String(layer?.sourceKey ?? "") : undefined,

    style:
      type === "text"
        ? {
            fontFamily: style.fontFamily ?? FONT_TNR,
            fontSize: Number(style.fontSize ?? 18),
            fontWeight: String(style.fontWeight ?? "400"),
            color: style.color ?? "#ffffff",
            align: style.align ?? "left",
            uppercase: !!style.uppercase,
            lineHeight: Number(style.lineHeight ?? 1.25),
          }
        : undefined,
  };

  if (out.type === "image" && out.circle) {
    out.square = true;
    out.height = squareHeightPct(out.width);
  }

  return out;
}

export const createEventInvitationTextLayer = (patch = {}) =>
  normalizeEventInvitationLayer({
    id: genEventInvitationLayerId("txt"),
    type: "text",
    top: 60,
    left: 10,
    width: 60,
    height: 4,
    zIndex: 50,
    text: "Chữ mới",
    style: {
      fontFamily: FONT_TNR,
      fontSize: 18,
      fontWeight: "600",
      color: "#ffffff",
      align: "left",
      uppercase: false,
      lineHeight: 1.25,
    },
    ...patch,
  });

export const createEventInvitationImageLayer = (patch = {}) =>
  normalizeEventInvitationLayer({
    id: genEventInvitationLayerId("img"),
    type: "image",
    top: 60,
    left: 10,
    width: 20,
    height: 12,
    zIndex: 50,
    sourceKey: "",
    src: "",
    fit: "cover",
    circle: false,
    square: false,
    ...patch,
  });

export function ensureEventInvitationSchemaV2(loaded) {
  const s = {
    ...DEFAULT_EVENT_INVITATION_SCHEMA,
    ...(loaded || {}),
    schemaVersion: 2,
  };

  if (Array.isArray(s.layers) && s.layers.length) {
    s.layers = s.layers
      .map((l) => normalizeEventInvitationLayer(l))
      .sort((a, b) => Number(a.zIndex) - Number(b.zIndex));

    return s;
  }

  s.layers = [
    normalizeEventInvitationLayer({
      id: "txt_title",
      type: "text",
      top: 10,
      left: 8,
      width: 84,
      height: 5,
      zIndex: 20,
      text: EVENT_TEMPLATE_VARS.title.token,
      style: {
        fontFamily: FONT_TNR,
        fontSize: 30,
        fontWeight: "800",
        color: "#ffffff",
        align: "center",
        uppercase: true,
        lineHeight: 1.15,
      },
    }),

    normalizeEventInvitationLayer({
      id: "txt_topic",
      type: "text",
      top: 16,
      left: 8,
      width: 84,
      height: 8,
      zIndex: 21,
      text: EVENT_TEMPLATE_VARS.topic.token,
      style: {
        fontFamily: FONT_TNR,
        fontSize: 24,
        fontWeight: "700",
        color: "#ffffff",
        align: "center",
        uppercase: true,
        lineHeight: 1.2,
      },
    }),

    normalizeEventInvitationLayer({
      id: "txt_content",
      type: "text",
      top: 32,
      left: 10,
      width: 80,
      height: 25,
      zIndex: 22,
      text: EVENT_TEMPLATE_VARS.content.token,
      style: {
        fontFamily: FONT_TNR,
        fontSize: 18,
        fontWeight: "400",
        color: "#ffffff",
        align: "left",
        uppercase: false,
        lineHeight: 1.35,
      },
    }),

    normalizeEventInvitationLayer({
      id: "txt_time",
      type: "text",
      top: 62,
      left: 10,
      width: 80,
      height: 5,
      zIndex: 23,
      text: EVENT_TEMPLATE_VARS.time.token,
      style: {
        fontFamily: FONT_TNR,
        fontSize: 20,
        fontWeight: "700",
        color: "#ffffff",
        align: "center",
        uppercase: false,
        lineHeight: 1.2,
      },
    }),

    normalizeEventInvitationLayer({
      id: "txt_location",
      type: "text",
      top: 68,
      left: 10,
      width: 80,
      height: 6,
      zIndex: 24,
      text: EVENT_TEMPLATE_VARS.location.token,
      style: {
        fontFamily: FONT_TNR,
        fontSize: 19,
        fontWeight: "700",
        color: "#ffffff",
        align: "center",
        uppercase: false,
        lineHeight: 1.2,
      },
    }),

    normalizeEventInvitationLayer({
      id: "img_qrCode",
      type: "image",
      top: 76,
      left: 39,
      width: 22,
      height: squareHeightPct(22),
      zIndex: 26,
      sourceKey: "event.qrCode",
      fit: "contain",
      circle: false,
      square: true,
    }),

    normalizeEventInvitationLayer({
      id: "txt_note",
      type: "text",
      top: 92,
      left: 10,
      width: 80,
      height: 5,
      zIndex: 25,
      text: EVENT_TEMPLATE_VARS.note.token,
      style: {
        fontFamily: FONT_TNR,
        fontSize: 16,
        fontWeight: "400",
        color: "#ffffff",
        align: "center",
        uppercase: false,
        lineHeight: 1.25,
      },
    }),
  ];

  return s;
}

/* ---------- Render layers ---------- */
function LayerText({ layer, eventData }) {
  const st = layer.style || {};

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        fontFamily: st.fontFamily ?? FONT_TNR,
        fontSize: st.fontSize ?? 18,
        fontWeight: st.fontWeight ?? "400",
        color: st.color ?? "#fff",
        textAlign: st.align ?? "left",
        lineHeight: st.lineHeight ?? 1.25,
        textTransform: st.uppercase ? "uppercase" : "none",
        whiteSpace: "pre-wrap",
        overflow: "hidden",
      }}
    >
      {renderTemplatePreviewText(layer.text || "", eventData)}
    </div>
  );
}

function LayerImage({ layer, resolvedSrc }) {
  const src = resolvedSrc || layer.src || "";
  const circle = !!layer.circle;
  const isQrPlaceholder = layer.sourceKey === "event.qrCode" && !src;

  return (
    <div
      data-bg-src={src}
      style={{
        width: "100%",
        height: "100%",
        borderRadius: circle ? "9999px" : "12px",
        overflow: "hidden",
        backgroundColor: src ? "transparent" : "rgba(255,255,255,0.25)",
        backgroundImage: src ? `url("${src}")` : "none",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: layer.fit === "contain" ? "contain" : "cover",
        border: src ? "none" : "1px dashed rgba(255,255,255,0.6)",
      }}
    >
      {!src && (
        <div className="w-full h-full flex items-center justify-center text-white/80 text-sm font-semibold text-center px-2">
          {isQrPlaceholder ? (
            "Ảnh QR"
          ) : (
            <PhotoIcon className="w-8 h-8" />
          )}
        </div>
      )}
    </div>
  );
}

function PageSingleEditable({
  schema,
  previewData,
  editorMode = false,
  cardHeight,
  onCardHeightChange,
}) {
  const s = ensureEventInvitationSchemaV2(schema);
  const layers = s.layers || [];
  const bgImgRef = useRef(null);

  const handleBgLoad = () => {
    const h = bgImgRef.current?.offsetHeight || 0;
    onCardHeightChange?.(h);
  };

  useLayoutEffect(() => {
    if (!bgImgRef.current) return;

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            const h = bgImgRef.current?.offsetHeight || 0;
            onCardHeightChange?.(h);
          })
        : null;

    if (ro) ro.observe(bgImgRef.current);

    return () => ro?.disconnect?.();
  }, [onCardHeightChange]);

  const cardH = Number(cardHeight || 0);

  return (
    <div className="relative" style={{ width: BASE_MD_WIDTH }}>
      <img
        ref={bgImgRef}
        src={s.backgroundSrc || DEFAULT_EVENT_INVITATION_SCHEMA.backgroundSrc}
        className="block w-full h-auto"
        alt=""
        draggable={false}
        onLoad={handleBgLoad}
      />

      {layers
        .slice()
        .sort((a, b) => Number(a.zIndex ?? 1) - Number(b.zIndex ?? 1))
        .map((layer) => {
          const hasBottom = layer.bottom !== undefined && layer.bottom !== null;

          const leftPx = (BASE_MD_WIDTH * Number(layer.left ?? 0)) / 100;
          const widthPx = (BASE_MD_WIDTH * Number(layer.width ?? 10)) / 100;

          const heightPx =
            layer.type === "image" && (layer.circle || layer.square)
              ? widthPx
              : cardH
                ? (cardH * Number(layer.height ?? 6)) / 100
                : (BASE_MD_WIDTH * Number(layer.height ?? 6)) / 100;

          const topPx =
            !hasBottom && cardH ? (cardH * Number(layer.top ?? 0)) / 100 : 0;
          const bottomPx =
            hasBottom && cardH ? (cardH * Number(layer.bottom ?? 0)) / 100 : 0;

          const wrapperStyle = {
            position: "absolute",
            left: `${leftPx}px`,
            width: `${widthPx}px`,
            height: `${heightPx}px`,
            zIndex: layer.zIndex ?? 1,
            pointerEvents: editorMode ? "none" : "auto",
            ...(hasBottom
              ? { bottom: `${bottomPx}px` }
              : { top: `${topPx}px` }),
          };

          if (layer.type === "image") {
            const resolvedSrc = resolveImageSource(
              layer.sourceKey,
              previewData,
            );

            return (
              <div key={layer.id} style={wrapperStyle}>
                <LayerImage layer={layer} resolvedSrc={resolvedSrc} />
              </div>
            );
          }

          return (
            <div key={layer.id} style={wrapperStyle}>
              <LayerText layer={layer} eventData={previewData} />
            </div>
          );
        })}
    </div>
  );
}

/* ---------- Drag overlay ---------- */
function useDrag({ onStart, onMove, onEnd }) {
  const draggingRef = useRef(false);
  const startRef = useRef({ x: 0, y: 0 });

  const onPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    draggingRef.current = true;
    startRef.current = { x: e.clientX, y: e.clientY };

    onStart?.(e);

    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    e.preventDefault();

    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    startRef.current = { x: e.clientX, y: e.clientY };

    onMove?.({ dx, dy });
  };

  const onPointerUp = (e) => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    onEnd?.(e);
  };

  return { onPointerDown, onPointerMove, onPointerUp };
}

const LayerBox = ({ layer, selected, onSelect, onDrag, cardHeight }) => {
  const hasBottom = layer.bottom !== undefined && layer.bottom !== null;
  const isSquareImg = layer.type === "image" && (layer.circle || layer.square);

  const leftPx = (BASE_MD_WIDTH * Number(layer.left ?? 0)) / 100;
  const widthPx = (BASE_MD_WIDTH * Number(layer.width ?? 10)) / 100;

  const heightPx = isSquareImg
    ? widthPx
    : cardHeight
      ? (cardHeight * Number(layer.height ?? 6)) / 100
      : (BASE_MD_WIDTH * Number(layer.height ?? 6)) / 100;

  const topPx =
    !hasBottom && cardHeight ? (cardHeight * Number(layer.top ?? 0)) / 100 : 0;

  const bottomPx =
    hasBottom && cardHeight
      ? (cardHeight * Number(layer.bottom ?? 0)) / 100
      : 0;

  const drag = useDrag({
    onStart: () => onSelect?.(layer.id),
    onMove: onDrag,
  });

  return (
    <div
      className="absolute"
      style={{
        left: `${leftPx}px`,
        width: `${widthPx}px`,
        height: `${heightPx}px`,
        top: !hasBottom ? `${topPx}px` : undefined,
        bottom: hasBottom ? `${bottomPx}px` : undefined,
        zIndex: 9999,
      }}
      {...drag}
    >
      <div
        className="absolute inset-0"
        style={{
          border: selected
            ? "2px solid #3b82f6"
            : "1px dashed rgba(255,255,255,0.65)",
          borderRadius: 10,
          background: selected ? "rgba(59,130,246,0.10)" : "rgba(0,0,0,0.02)",
          cursor: "move",
          touchAction: "none",
          userSelect: "none",
        }}
      />
      {selected && (
        <div
          className="absolute -top-7 left-0 px-2 py-1 text-xs rounded"
          style={{ background: "#3b82f6", color: "white" }}
        >
          {layer.type === "image" ? "Ảnh" : "Chữ"} · {layer.id}
        </div>
      )}
    </div>
  );
};

/* ---------- Template editor stage ---------- */
function TemplateEditorStage({ schema, setSchema, selected, setSelected }) {
  const cardRef = useRef(null);
  const s = ensureEventInvitationSchemaV2(schema);
  const layers = s.layers || [];

  const [cardHeight, setCardHeight] = useState(0);

  const updateLayer = (id, patch) => {
    setSchema((prev) => {
      const next = ensureEventInvitationSchemaV2(prev);

      next.layers = (next.layers || []).map((l) => {
        if (l.id !== id) return l;

        return normalizeEventInvitationLayer({ ...l, ...patch });
      });

      return next;
    });
  };

  const updateByDrag = (id, { dx, dy }) => {
    const base = cardRef.current;
    if (!base) return;

    const rect = base.getBoundingClientRect();
    const baseW = rect.width;
    const baseH = rect.height;

    const cur = layers.find((x) => x.id === id);
    if (!cur) return;

    const dLeftPct = toPct(dx, baseW);
    const dTopPct = toPct(dy, baseH);

    const nextLeft = clamp((cur.left ?? 0) + dLeftPct, 0, 100);

    if (cur.bottom !== undefined && cur.bottom !== null) {
      const nextBottom = clamp((cur.bottom ?? 0) - dTopPct, 0, 100);
      updateLayer(id, { left: nextLeft, bottom: nextBottom });
    } else {
      const nextTop = clamp((cur.top ?? 0) + dTopPct, 0, 100);
      updateLayer(id, { left: nextLeft, top: nextTop });
    }
  };

  return (
    <div className="w-full" onClick={() => setSelected(null)}>
      <ScaledStage>
        <div
          ref={cardRef}
          className="relative"
          style={{ width: BASE_MD_WIDTH }}
          onClick={(e) => e.stopPropagation()}
        >
          <PageSingleEditable
            schema={s}
            previewData={MOCK_EVENT}
            editorMode
            cardHeight={cardHeight}
            onCardHeightChange={(h) => setCardHeight(h)}
          />

          {layers.map((layer) => (
            <LayerBox
              key={layer.id}
              layer={layer}
              selected={selected === layer.id}
              onSelect={setSelected}
              onDrag={(d) => updateByDrag(layer.id, d)}
              cardHeight={cardHeight}
            />
          ))}
        </div>
      </ScaledStage>
    </div>
  );
}

/* ---------- Right panel controls ---------- */
function ElementControls({
  schema,
  setSchema,
  selected,
  setSelected,
  onPickLayerImage,
}) {
  const s = ensureEventInvitationSchemaV2(schema);
  const layers = s.layers || [];
  const layer = layers.find((l) => l.id === selected);

  const [quickFieldKey, setQuickFieldKey] = useState(
    FIELD_DEFS[0]?.key || "event.title",
  );
  const [selectedFieldKey, setSelectedFieldKey] = useState(
    FIELD_DEFS[0]?.key || "event.title",
  );

  useEffect(() => {
    setSelectedFieldKey(FIELD_DEFS[0]?.key || "event.title");
  }, [selected]);

  const setLayer = (patch) => {
    if (!selected) return;

    setSchema((prev) => {
      const next = ensureEventInvitationSchemaV2(prev);

      next.layers = (next.layers || []).map((l) => {
        if (l.id !== selected) return l;

        return normalizeEventInvitationLayer({ ...l, ...patch });
      });

      return next;
    });
  };

  const deleteLayer = () => {
    if (!selected) return;

    setSchema((prev) => {
      const next = ensureEventInvitationSchemaV2(prev);
      next.layers = (next.layers || []).filter((l) => l.id !== selected);
      return next;
    });

    setSelected?.(null);
  };

  const insertFieldAsText = () => {
    const def =
      FIELD_DEFS.find((x) => x.key === quickFieldKey) || FIELD_DEFS[0];

    const value = resolveFieldValue(def.key);

    const newLayer = normalizeEventInvitationLayer({
      id: genEventInvitationLayerId("txt"),
      type: "text",
      top: 60,
      left: 10,
      width: 60,
      height: 4,
      zIndex: (layers?.length || 0) + 50,
      text: `${def.prefix || ""}${value}`,
      style: {
        fontFamily: FONT_TNR,
        fontSize: 18,
        fontWeight: "400",
        color: "#ffffff",
        align: "left",
        uppercase: false,
        lineHeight: 1.25,
      },
    });

    setSchema((prev) => {
      const next = ensureEventInvitationSchemaV2(prev);
      next.layers = [...(next.layers || []), newLayer];
      return next;
    });

    setSelected?.(newLayer.id);
    toast.info("Đã chèn thông tin sự kiện.");
  };

  const applySelectedFromField = (fieldKey) => {
    if (!layer || layer.type !== "text") return;

    const def = FIELD_DEFS.find((x) => x.key === fieldKey);
    if (!def) return;

    const value = resolveFieldValue(def.key);
    setLayer({ text: `${def.prefix || ""}${value}` });
    toast.info("Đã auto điền nội dung.");
  };

  const st = layer?.type === "text" ? layer.style || {} : null;
  const hasBottom = layer?.bottom !== undefined && layer?.bottom !== null;
  const isSquareImg = layer?.type === "image" && (layer?.circle || layer?.square);

  if (!selected || !layer) {
    return (
      <div className="space-y-4">
        <div className="text-sm text-gray-500">
          Chọn một khối trên thiệp để chỉnh.
        </div>

        <div className="border-t pt-4 space-y-2">
          <div className="font-semibold">Chèn thông tin sự kiện</div>
          <Select
            value={quickFieldKey}
            onChange={(e) => setQuickFieldKey(e.target.value)}
          >
            {FIELD_DEFS.map((f) => (
              <option key={f.key} value={f.key}>
                {f.label}
              </option>
            ))}
          </Select>

          <button
            className="btn btn-outline btn-primary w-full"
            onClick={insertFieldAsText}
          >
            <PlusIcon className="w-5 h-5 mr-1" />
            Chèn vào thiệp
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold">
          Đang chỉnh:{" "}
          <span className="text-blue-600">
            {layer.type === "image" ? "ẢNH" : "CHỮ"}
          </span>
        </div>
        <button
          className="btn btn-outline btn-error btn-sm"
          onClick={deleteLayer}
          title="Xoá khối này"
        >
          <TrashIcon className="w-4 h-4 mr-1" />
          Xoá
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="label">
            <span className="label-text">Trái (%)</span>
          </div>
          <Input
            type="number"
            value={layer.left ?? 0}
            onChange={(e) => setLayer({ left: Number(e.target.value) })}
          />
        </div>

        {!hasBottom ? (
          <div>
            <div className="label">
              <span className="label-text">Trên (%)</span>
            </div>
            <Input
              type="number"
              value={layer.top ?? 0}
              onChange={(e) => setLayer({ top: Number(e.target.value) })}
            />
          </div>
        ) : (
          <div>
            <div className="label">
              <span className="label-text">Dưới (%)</span>
            </div>
            <Input
              type="number"
              value={layer.bottom ?? 0}
              onChange={(e) => setLayer({ bottom: Number(e.target.value) })}
            />
          </div>
        )}

        <div className="col-span-2">
          <div className="label">
            <span className="label-text">Rộng (% theo chiều rộng)</span>
          </div>
          <Input
            type="number"
            value={layer.width ?? 20}
            onChange={(e) => {
              const w = Number(e.target.value);

              if (layer.type === "image" && (layer.circle || layer.square)) {
                setLayer({ width: w, height: squareHeightPct(w) });
              } else {
                setLayer({ width: w });
              }
            }}
          />
        </div>

        <div className="col-span-2">
          <div className="label">
            <span className="label-text">Cao (% theo chiều cao)</span>
          </div>
          <Input
            type="number"
            value={isSquareImg ? squareHeightPct(layer.width ?? 20) : (layer.height ?? 6)}
            disabled={isSquareImg}
            onChange={(e) => setLayer({ height: Number(e.target.value) })}
          />
          {isSquareImg && (
            <div className="text-xs text-gray-500 mt-1">
              Ảnh vuông sẽ tự tính chiều cao theo chiều rộng.
            </div>
          )}
        </div>

        <div className="col-span-2">
          <div className="label">
            <span className="label-text">Thứ tự lớp</span>
          </div>
          <Input
            type="number"
            value={layer.zIndex ?? 10}
            onChange={(e) => setLayer({ zIndex: Number(e.target.value) })}
          />
        </div>
      </div>

      {layer.type === "image" ? (
        <div className="space-y-2 pt-2 border-t">
          <div className="font-semibold">Ảnh</div>

          <div>
            <div className="label">
              <span className="label-text">Nguồn ảnh nhanh</span>
            </div>
            <Select
              value={layer.sourceKey ?? ""}
              onChange={(e) => {
                const sourceKey = e.target.value;

                if (sourceKey === "event.qrCode") {
                  setLayer({
                    sourceKey,
                    square: true,
                    height: squareHeightPct(layer.width ?? 20),
                  });
                } else {
                  setLayer({
                    sourceKey,
                    square: false,
                  });
                }
              }}
            >
              {IMAGE_SOURCES.map((x) => (
                <option key={x.key} value={x.key}>
                  {x.label}
                </option>
              ))}
            </Select>
          </div>

          {!(layer.sourceKey || "").trim() && (
            <>
              <div>
                <div className="label">
                  <span className="label-text">Đường dẫn ảnh</span>
                </div>
                <Input
                  value={layer.src ?? ""}
                  onChange={(e) => setLayer({ src: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <button
                type="button"
                className="btn btn-outline btn-info btn-sm"
                onClick={onPickLayerImage}
              >
                Chọn ảnh từ thư viện
              </button>
            </>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="label">
                <span className="label-text">Kiểu hiển thị</span>
              </div>
              <Select
                value={layer.fit === "contain" ? "contain" : "cover"}
                onChange={(e) => setLayer({ fit: e.target.value })}
              >
                <option value="cover">Phủ kín</option>
                <option value="contain">Giữ nguyên tỷ lệ</option>
              </Select>
            </div>

            <div className="flex items-end">
              <label className="label cursor-pointer gap-2">
                <span className="label-text">Bo tròn</span>
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={!!layer.circle}
                  onChange={(e) => {
                    const c = e.target.checked;
                    if (c) {
                      setLayer({
                        circle: true,
                        square: true,
                        height: squareHeightPct(layer.width ?? 20),
                      });
                    } else {
                      setLayer({
                        circle: false,
                        square: layer.sourceKey === "event.qrCode",
                        height:
                          layer.sourceKey === "event.qrCode"
                            ? squareHeightPct(layer.width ?? 20)
                            : layer.height,
                      });
                    }
                  }}
                />
              </label>
            </div>
          </div>

          <div className="text-xs text-gray-500 break-words">
            Ảnh xem trước:{" "}
            <span className="text-gray-700">{getImagePreviewText(layer)}</span>
          </div>
        </div>
      ) : (
        <div className="space-y-2 pt-2 border-t">
          <div className="font-semibold">Chữ</div>

          <div className="p-3 rounded-lg border bg-base-100 space-y-2">
            <div className="font-semibold text-sm">
              Auto điền từ dữ liệu sự kiện
            </div>
            <Select
              value={selectedFieldKey}
              onChange={(e) => setSelectedFieldKey(e.target.value)}
            >
              {FIELD_DEFS.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </Select>

            <button
              className="btn btn-outline btn-primary w-full"
              onClick={() => applySelectedFromField(selectedFieldKey)}
            >
              Áp dụng vào khối đang chọn
            </button>
          </div>

          <div>
            <div className="label">
              <span className="label-text">Nội dung</span>
            </div>
            <textarea
              className="textarea textarea-bordered w-full min-h-[88px]"
              value={layer.text ?? ""}
              onChange={(e) => setLayer({ text: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="label">
                <span className="label-text">Cỡ chữ</span>
              </div>
              <Input
                type="number"
                value={Number(st.fontSize ?? 18)}
                onChange={(e) =>
                  setLayer({
                    style: { ...st, fontSize: Number(e.target.value) },
                  })
                }
              />
            </div>

            <div>
              <div className="label">
                <span className="label-text">Độ đậm chữ</span>
              </div>
              <Select
                value={String(st.fontWeight ?? "400")}
                onChange={(e) =>
                  setLayer({ style: { ...st, fontWeight: e.target.value } })
                }
              >
                {[
                  "100",
                  "200",
                  "300",
                  "400",
                  "500",
                  "600",
                  "700",
                  "800",
                  "900",
                ].map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <div className="label">
                <span className="label-text">Canh chữ</span>
              </div>
              <Select
                value={st.align ?? "left"}
                onChange={(e) =>
                  setLayer({ style: { ...st, align: e.target.value } })
                }
              >
                <option value="left">Trái</option>
                <option value="center">Giữa</option>
                <option value="right">Phải</option>
              </Select>
            </div>

            <div>
              <div className="label">
                <span className="label-text">Màu chữ</span>
              </div>
              <Input
                value={st.color ?? "#ffffff"}
                onChange={(e) =>
                  setLayer({ style: { ...st, color: e.target.value } })
                }
              />
            </div>

            <div className="col-span-2">
              <div className="label">
                <span className="label-text">Kiểu chữ</span>
              </div>
              <Input
                value={st.fontFamily ?? FONT_TNR}
                onChange={(e) =>
                  setLayer({ style: { ...st, fontFamily: e.target.value } })
                }
              />
            </div>

            <div className="col-span-2">
              <div className="label">
                <span className="label-text">Giãn dòng</span>
              </div>
              <Input
                type="number"
                step="0.05"
                value={Number(st.lineHeight ?? 1.25)}
                onChange={(e) =>
                  setLayer({
                    style: { ...st, lineHeight: Number(e.target.value) },
                  })
                }
              />
            </div>

            <div className="col-span-2">
              <label className="label cursor-pointer gap-2">
                <span className="label-text">Viết hoa</span>
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={!!st.uppercase}
                  onChange={(e) =>
                    setLayer({ style: { ...st, uppercase: e.target.checked } })
                  }
                />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const EventInvitationCardTemplateEditor = ({
  schema,
  setSchema,
  selected,
  setSelected,
  metaForm,
  setMetaForm,
  categories,
  editing,
}) => {
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState("background");

  const updateBackground = (src) => {
    setSchema((prev) => {
      const next = ensureEventInvitationSchemaV2(prev);
      next.backgroundSrc = src;
      return next;
    });
  };

  const updateSelectedImage = (src) => {
    if (!selected) return;

    setSchema((prev) => {
      const next = ensureEventInvitationSchemaV2(prev);

      next.layers = (next.layers || []).map((layer) => {
        if (layer.id !== selected) return layer;

        return normalizeEventInvitationLayer({
          ...layer,
          sourceKey: "",
          src,
        });
      });

      return next;
    });
  };

  const openPicker = (target) => {
    setPickerTarget(target);
    setIsImageModalOpen(true);
  };

  const closePicker = () => setIsImageModalOpen(false);

  const handlePressImage = (media) => {
    setIsImageModalOpen(false);

    if (pickerTarget === "background") {
      updateBackground(media.url);
    } else {
      updateSelectedImage(media.url);
    }
  };

  const s = ensureEventInvitationSchemaV2(schema);

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8">
          <div className="p-3 rounded-xl border bg-base-100">
            <TemplateEditorStage
              schema={schema}
              setSchema={setSchema}
              selected={selected}
              setSelected={setSelected}
            />

            <div className="mt-2 text-xs text-gray-500">
              Mẹo: Bấm khung nét đứt để chọn. Giữ/kéo để di chuyển.
            </div>
          </div>
        </div>

        <div className="lg:col-span-4 lg:self-start">
          <div className="p-4 rounded-xl border bg-base-100 space-y-4 lg:max-h-[calc(92vh-190px)] lg:overflow-y-auto lg:pr-3">
            <div className="font-semibold">Thông tin mẫu</div>

            <div className="grid grid-cols-1 gap-2">
              <div>
                <div className="label">
                  <span className="label-text">Danh mục hoạt động</span>
                </div>
                <Select
                  value={metaForm.categoryId}
                  onChange={(e) =>
                    setMetaForm((s) => ({
                      ...s,
                      categoryId: e.target.value,
                    }))
                  }
                >
                  <option value="">Mẫu mặc định</option>
                  {categories.map((cate) => (
                    <option key={cate.id} value={cate.id}>
                      {cate.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <div className="label">
                  <span className="label-text">Mã</span>
                </div>
                <Input
                  value={metaForm.code}
                  onChange={(e) =>
                    setMetaForm((s) => ({ ...s, code: e.target.value }))
                  }
                  placeholder="Mã"
                  disabled={!!editing?.id}
                />
              </div>

              <div>
                <div className="label">
                  <span className="label-text">Tên mẫu</span>
                </div>
                <Input
                  value={metaForm.name}
                  onChange={(e) =>
                    setMetaForm((s) => ({ ...s, name: e.target.value }))
                  }
                  placeholder="Thiệp sự kiện v1"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="label">
                    <span className="label-text">Phiên bản</span>
                  </div>
                  <Input
                    type="number"
                    min={1}
                    value={metaForm.version}
                    onChange={(e) =>
                      setMetaForm((s) => ({ ...s, version: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <div className="label">
                    <span className="label-text">Trạng thái</span>
                  </div>
                  <Select
                    value={metaForm.isActive ? "1" : "0"}
                    onChange={(e) =>
                      setMetaForm((s) => ({
                        ...s,
                        isActive: e.target.value === "1",
                      }))
                    }
                  >
                    <option value="1">Đang dùng</option>
                    <option value="0">Tắt</option>
                  </Select>
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="font-semibold mb-2">Ảnh nền thiệp</div>

                <div className="space-y-2">
                  <Input
                    value={s.backgroundSrc || ""}
                    onChange={(e) => updateBackground(e.target.value)}
                    placeholder="Đường dẫn ảnh"
                  />

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-outline btn-info btn-sm"
                      onClick={() => openPicker("background")}
                    >
                      Chọn ảnh nền từ thư viện
                    </button>

                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() =>
                        updateBackground(
                          DEFAULT_EVENT_INVITATION_SCHEMA.backgroundSrc,
                        )
                      }
                    >
                      Về mặc định
                    </button>
                  </div>

                  <div className="relative w-full border rounded-lg overflow-hidden bg-base-200">
                    {s.backgroundSrc ? (
                      <img
                        src={s.backgroundSrc}
                        alt="Ảnh nền"
                        className="w-full h-32 object-contain"
                      />
                    ) : (
                      <div className="w-full h-32 flex items-center justify-center text-gray-400">
                        Chưa có ảnh nền
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <ElementControls
                schema={schema}
                setSchema={setSchema}
                selected={selected}
                setSelected={setSelected}
                onPickLayerImage={() => openPicker("layer")}
              />
            </div>

            <div className="border-t pt-4 text-xs text-gray-500">
              Ghi chú: Rộng là % theo chiều rộng; Cao là % theo chiều cao.
              Dữ liệu động dùng token như {"{{title}}"}, {"{{time}}"},{" "}
              {"{{content}}"}, {"{{qrCode}}"}.
            </div>
          </div>
        </div>
      </div>

      {isImageModalOpen && (
        <ImageManagerModal
          onClose={closePicker}
          onPress={handlePressImage}
          multiImage={true}
          selectedMultiMedia={[]}
          deActiveInsertImage={true}
        />
      )}
    </>
  );
};

export default EventInvitationCardTemplateEditor;